import { ElMessage } from 'element-plus';

// 文件格式映射表 - 将相似格式合并为一个友好名称
export const formatMap: Record<string, string> = {
  // 图片格式
  'jpeg': 'JPG',
  'jpg': 'JPG',
  'png': 'PNG',
  'gif': 'GIF',
  'bmp': 'BMP',
  'webp': 'WebP',
  'svg': 'SVG',

  // 文档格式
  'doc': 'Word',
  'docx': 'Word',
  'dot': 'Word',
  'dotx': 'Word',

  // 表格格式
  'xls': 'Excel',
  'xlsx': 'Excel',
  'xlt': 'Excel',
  'xltx': 'Excel',
  'csv': 'CSV',

  // 演示文稿格式
  'ppt': 'PPT',
  'pptx': 'PPT',
  'pot': 'PPT',
  'potx': 'PPT',

  // PDF格式
  'pdf': 'PDF',

  // 文本格式
  'txt': '文本',
  'rtf': '富文本',

  // 压缩格式
  'zip': '压缩包',
  'rar': '压缩包',
  '7z': '压缩包',
  'tar': '压缩包',
  'gz': '压缩包',

  // 音频格式
  'mp3': '音频',
  'wav': '音频',
  'ogg': '音频',
  'flac': '音频',
  'aac': '音频',

  // 视频格式
  'mp4': '视频',
  'avi': '视频',
  'mov': '视频',
  'wmv': '视频',
  'flv': '视频',
  'webm': '视频'
};

// 获取文件扩展名
export const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex > -1 ? filename.slice(lastDotIndex + 1).toLowerCase() : '';
};

// 根据扩展名获取友好的文件类型名称
export const getFileTypeName = (filename: string): string => {
  const extension = getFileExtension(filename);
  return formatMap[extension] || '其他';
};

// 检查文件是否为图片类型
export const isImageFile = (filename: string): boolean => {
  const extension = getFileExtension(filename);
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  return imageExtensions.includes(extension);
};

// 计算文件格式提示文本
export const getFormatTip = (accept: string[] = [], separator: string = '/'): string => {
  // 获取所有唯一的格式名称
  const uniqueFormats = new Set<string>();

  // 确保accept是数组且不为空
  if (Array.isArray(accept) && accept.length > 0) {
    accept.forEach(ext => {
      if (ext && typeof ext === 'string') {
        // 移除点号并转换为小写
        const format = ext.replace('.', '').toLowerCase();
        // 使用映射表中的名称，如果不存在则使用原始格式
        const displayName = formatMap[format] || format;
        uniqueFormats.add(displayName);
      }
    });
  }

  // 将唯一格式名称用指定分隔符连接
  return Array.from(uniqueFormats).join(separator);
};

// 文件上传前校验函数
export const beforeUpload = (file: File, maxSize: number, accept: string[], formatTip: string): boolean => {
  // 检查文件大小
  const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
  if (!isLtMaxSize) {
    ElMessage.error(`文件大小不能超过${maxSize}MB!`);
    return false;
  }

  // 检查文件格式（根据accept属性）
  const allowedExtensions = accept.map(ext => ext.toLowerCase());
  const fileExtension = '.' + getFileExtension(file.name);
  const isSupportedType = fileExtension && allowedExtensions.includes(fileExtension);

  if (!isSupportedType) {
    ElMessage.error(`不支持的文件格式，请上传${formatTip}格式的文件!`);
    return false;
  }

  return true;
};

// 进度计算相关类型
export interface FileProgressInfo {
  uid: string;
  progress: number;
}

// 创建文件进度映射表
export const createFileProgressMap = (files: (File | any)[]): Map<string, number> => {
  const fileProgressMap = new Map<string, number>();
  files.forEach(file => {
    if (file && file.uid) {
      fileProgressMap.set(file.uid, 0);
    }
  });
  return fileProgressMap;
};

// 计算总体进度（使用加权平均：已完成文件占80%权重，进行中文件占20%权重）
export const calculateOverallProgress = (
  fileProgressMap: Map<string, number>, 
  totalFileCount: number
): number => {
  if (fileProgressMap.size === 0) return 0;

  let totalProgress = 0;
  let completedFiles = 0;

  fileProgressMap.forEach((progress, fileId) => {
    totalProgress += progress;
    if (progress === 100) {
      completedFiles++;
    }
  });

  // 使用加权平均计算总体进度：已完成文件占80%权重，进行中文件占20%权重
  const completedWeight = 0.8;
  const inProgressWeight = 0.2;

  const completedProgress = (completedFiles / totalFileCount) * 100 * completedWeight;
  const inProgressProgress = (totalProgress / fileProgressMap.size) * inProgressWeight;

  return Math.round(completedProgress + inProgressProgress);
};

// 更新单个文件进度
export const updateFileProgress = (
  fileProgressMap: Map<string, number>,
  fileInfo: { uid: string },
  progress: number
): void => {
  if (fileInfo && fileInfo.uid) {
    fileProgressMap.set(fileInfo.uid, (progress * 100) || 0);
  }
};

// 进度事件处理函数工厂
export const createProgressHandler = (
  fileProgressMap: Map<string, number>,
  totalFileCount: number,
  onProgressUpdate: (progress: number) => void
) => {
  return (progressEvent: any) => {
    const { fileInfo, progress } = progressEvent;
    if (fileInfo) {
      // 更新该文件的进度
      updateFileProgress(fileProgressMap, fileInfo, progress);
      // 计算并更新总体进度
      const overallProgress = calculateOverallProgress(fileProgressMap, totalFileCount);
      onProgressUpdate(Math.min(overallProgress, 100));
    }
  };
};